import React from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle, Clock, LogOut } from 'lucide-react';

import OscopLogo from '../../components/brand/OscopLogo';

import AnnouncementsBanner from '../../components/AnnouncementsBanner';

import NotificationBell from '../../components/NotificationBell';

import LanguageSelector from '../../components/LanguageSelector';

import HelpCenterV2 from '../../components/HelpCenterV2';
import WelcomeTour from '../../components/WelcomeTour';
import AdhesionFTPEBanner from '../../components/AdhesionFTPEBanner';
import BackButton from '../../components/BackButton';

import CreditBanner from '../../components/CreditBanner';

import { useExpertDashboard } from './useExpertDashboard';
import ExpertTabs from './ExpertTabs';

export default function ExpertDashboard() {
  const ctx = useExpertDashboard();
  const { user, navigate, activeTab, setActiveTab, loading, sidebarOpen, setSidebarOpen, expertProfile, handleLogout, sidebarTabs } = ctx;

  // Si compte non validé
  if (user?.statut !== 'validé') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full shadow-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center">
              <Clock className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Compte en Attente de Validation</CardTitle>
            <CardDescription className="text-base mt-2">
              Votre inscription en tant que Conseiller Stratégique est en cours de validation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <p className="text-amber-900 mb-4">
                Votre compte Conseiller a été créé avec succès et est actuellement en attente de validation par un administrateur COPPAM.
              </p>
              <div className="space-y-2 text-amber-800">
                <p><strong>Prochaines étapes :</strong></p>
                <ol className="list-decimal list-inside space-y-1 ml-4">
                  <li>Un administrateur examinera votre demande</li>
                  <li>Vous recevrez un email une fois votre compte validé</li>
                  <li>Vous pourrez alors accéder à votre espace Conseiller</li>
                </ol>
              </div>
            </div>
            <div className="mt-6 text-center">
              <Button onClick={handleLogout} variant="outline">
                <LogOut className="mr-2 h-4 w-4" />
                Se déconnecter
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Bandeau Crédits ESS */}
      <CreditBanner />
      
      {/* Bandeau Adhésion FTPE */}
      <AdhesionFTPEBanner />
      
      {/* Bouton Retour */}
      <div className="p-4 bg-white border-b">
        <BackButton label="Retour" />
      </div>
      
      <div className="flex flex-1">
      {/* Sidebar - Version améliorée et responsive */}
      <aside className={`
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        fixed lg:relative lg:translate-x-0
        w-72 h-screen bg-white border-r border-slate-200
        transition-transform duration-300 ease-in-out
        z-30 flex flex-col
      `}>
        {/* Logo et Profil Expert — Premium navy/or */}
        <div className="relative p-5 border-b border-slate-200 bg-gradient-to-br from-[#0a3d62] to-[#0c4a78] overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")" }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-11 w-11 rounded-xl bg-[#DEA900] flex items-center justify-center shadow-md shadow-[#DEA900]/30 ring-1 ring-[#f5cc3e]/40">
                <OscopLogo size={28} variant="on-navy" />
              </div>
              <div>
                <div className="text-[9px] font-bold tracking-[0.18em] text-[#f5cc3e] uppercase">O'SCOP Outremer</div>
                <h2 className="font-bold text-white leading-tight">EXPERT</h2>
                <p className="text-[10px] text-white/60 italic uppercase tracking-wider">Technique &amp; Préventif</p>
              </div>
            </div>

            {expertProfile && (
              <div className="bg-white/95 rounded-lg p-3 shadow-sm backdrop-blur">
                <p className="text-sm font-semibold text-[#0a3d62]">{expertProfile.prenom} {expertProfile.nom}</p>
                <p className="text-xs text-slate-600 truncate">{expertProfile.email}</p>
                <Badge className="mt-2 text-xs bg-emerald-600 hover:bg-emerald-600 text-white shadow-sm font-semibold">
                  <CheckCircle className="mr-1 h-3 w-3" />
                  Compte Validé
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {sidebarTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setActiveTab(tab.value);
                setSidebarOpen(false); // Fermer la sidebar en mobile après sélection
              }}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-200
                ${activeTab === tab.value 
                  ? 'bg-[#0a3d62] text-white shadow-md ring-1 ring-[#DEA900]/40' 
                  : 'text-slate-700 hover:bg-[#0a3d62]/5 hover:text-[#0a3d62]'
                }
              `}
            >
              {tab.icon}
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>

        {/* Footer avec déconnexion */}
        <div className="p-4 border-t border-slate-200">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Overlay pour mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Contenu principal */}
      <main className="flex-1 flex flex-col min-h-screen lg:ml-0">
        {/* Barre de navigation supérieure */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          {/* Bouton menu mobile */}
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </Button>
            
            {/* Titre mobile */}
            <h1 className="text-lg font-bold text-slate-900 lg:hidden">COOPER'S Advisor</h1>
            
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-900 hidden sm:flex"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="hidden md:inline">Retour à l'accueil</span>
            </Button>
          </div>

          {/* Actions utilisateur */}
          <div className="flex items-center gap-3">
            <LanguageSelector variant="compact" />
            <NotificationBell />
            <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white shadow-md hidden md:flex font-semibold">
              <CheckCircle className="mr-1 h-3 w-3" />
              Conseiller Validé
            </Badge>
            <Button
              onClick={handleLogout}
              variant="destructive"
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </header>

        {/* Contenu de la page */}
        <div className="flex-1 p-4 lg:p-6">
        {/* Annonces & Communications (visibles par les Experts) */}
        <AnnouncementsBanner space="expert" />
        {/* Wallet & Crédits ESS */}
        <ExpertTabs ctx={ctx} />
        </div>
      </main>
      
      {/* Tour guidé de bienvenue */}
      <WelcomeTour space="expert" />
      
      {/* Centre d'aide amélioré */}
      <HelpCenterV2 space="expert" />
      </div>
    </div>
  );
}
